import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Reveal from "@/components/Reveal";

const servicesDetail = [
    {
        num: "01",
        title: "Living Room Design",
        category: "Residential",
        timeline: "6 – 10 weeks",
        priceRange: "₹2,200 – ₹4,500 / sq ft",
        image: "https://images.unsplash.com/photo-1600210491892-03d54c0aaf87?auto=format&fit=crop&w=1600&q=80",
        description: "The living room is the social heart of the home. We design layered spaces with conversation pieces, bespoke joinery, considered lighting and sculptural seating — all balanced to perform from a quiet morning coffee to a 30-person gathering.",
        includes: [
            "Spatial layout & 3D visualisation",
            "Custom-built TV unit & joinery",
            "Curated upholstery & soft furnishings",
            "Decorative & architectural lighting",
            "False ceiling & cove detail",
            "Accent walls, panelling & art curation",
        ],
    },
    {
        num: "02",
        title: "Bedroom Design",
        category: "Residential",
        timeline: "5 – 8 weeks",
        priceRange: "₹2,000 – ₹4,000 / sq ft",
        image: "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=1600&q=80",
        description: "Bedrooms designed as quiet sanctuaries — full-wall wardrobes, integrated lighting, soft palettes and acoustically considered detailing. We design master suites, kids' rooms and guest bedrooms with the same level of care.",
        includes: [
            "Full-height custom wardrobes",
            "Bedside joinery & bed back walls",
            "Mood lighting & reading lights",
            "Curtains, blackout & sheer layering",
            "Hand-tufted rugs & soft seating",
            "Acoustic treatment & insulation",
        ],
    },
    {
        num: "03",
        title: "Modular Kitchen",
        category: "Residential",
        timeline: "6 – 10 weeks",
        priceRange: "₹3,500 – ₹8,000 / sq ft",
        image: "https://images.unsplash.com/photo-1722605090433-41d1183a792d?auto=format&fit=crop&w=1600&q=80",
        description: "From compact L-shaped kitchens to chef-grade island kitchens — we engineer modular systems with German hardware, full-slab Italian marble or quartz, and chef-grade appliance integration tailored to how you cook.",
        includes: [
            "Hettich / Hafele German hardware",
            "Italian marble or quartz countertops",
            "Tall pantry units & appliance towers",
            "Custom backsplash & under-cabinet lighting",
            "Built-in oven, hob & chimney integration",
            "Sink, tap & water purifier specification",
        ],
    },
    {
        num: "04",
        title: "Bathroom Design",
        category: "Residential",
        timeline: "5 – 7 weeks",
        priceRange: "₹3,000 – ₹6,500 / sq ft",
        image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80",
        description: "Spa-grade master baths and powder rooms finished in full-slab stone, antique brass and concealed lighting. We coordinate plumbing, waterproofing and finishing as one accountable workflow.",
        includes: [
            "Full-slab stone or large-format tile",
            "Premium sanitaryware (Kohler, Duravit, TOTO)",
            "Concealed cisterns & rain shower systems",
            "Custom vanities & mirror lighting",
            "Waterproofing & plumbing rework",
            "Underfloor heating (optional)",
        ],
    },
    {
        num: "05",
        title: "Commercial Interiors",
        category: "Commercial",
        timeline: "10 – 24 weeks",
        priceRange: "₹2,500 – ₹6,000 / sq ft",
        image: "https://images.unsplash.com/photo-1758691736933-bb0f88fe2e0c?auto=format&fit=crop&w=1600&q=80",
        description: "Hospitality-led offices, F&B, retail and boutique workplaces. We design commercial interiors that elevate your brand, optimise operations and demonstrably improve retention and dwell time.",
        includes: [
            "Workplace strategy & space planning",
            "Brand-led interior architecture",
            "Acoustic & MEP coordination",
            "Custom workstations & meeting rooms",
            "Reception, café & breakout zones",
            "Compliance, vendor & site management",
        ],
    },
    {
        num: "06",
        title: "Full Home Renovation",
        category: "Residential",
        timeline: "12 – 24 weeks",
        priceRange: "₹2,800 – ₹6,500 / sq ft",
        image: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=1600&q=80",
        description: "End-to-end renovation under a single, accountable team — from civil work and electricals to bespoke joinery and final styling. Live in your new home, not in the chaos of building one.",
        includes: [
            "Civil, plumbing & electrical rework",
            "False ceiling, painting & flooring",
            "All custom joinery & furniture",
            "Lighting design & home automation",
            "All soft furnishings & art",
            "Snagging, handover & 5-year warranty",
        ],
    },
    {
        num: "07",
        title: "Home Theatre Design",
        category: "Residential",
        timeline: "8 – 14 weeks",
        priceRange: "₹4,500 – ₹12,000 / sq ft",
        image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1600&q=80",
        description: "A dedicated home theatre is the pinnacle of residential luxury. We design immersive private cinemas from the ground up — acoustic room treatment, 4K laser projection, Dolby Atmos surround sound, motorised seating and bespoke millwork — all calibrated by certified AV specialists.",
        includes: [
            "Acoustic room design & treatment",
            "4K / 8K laser projector & screen",
            "Dolby Atmos / DTS:X surround system",
            "Motorised recliner seating & risers",
            "Ambient & bias lighting control",
            "Smart home & AV integration",
        ],
    },
];

export default function Services() {
    return (
        <div data-testid="services-page" className="bg-charcoal text-cream">
            <section className="container-pad mx-auto pt-40 pb-20 md:pt-48 md:pb-24 border-b border-[#262626]">
                <Reveal>
                    <p className="overline mb-8">— Services</p>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tighter text-cream max-w-5xl">
                        Seven disciplines.<br />
                        <em className="italic text-gold">One standard.</em>
                    </h1>
                    <p className="mt-8 max-w-xl text-base md:text-lg text-[#A3A3A3]">
                        From a single living room refresh to a full 12,000 sq.ft penthouse —
                        each Midasis service is delivered by a single accountable team.
                    </p>
                </Reveal>
            </section>

            <section className="container-pad mx-auto py-20 md:py-28">
                <div className="space-y-32 md:space-y-40">
                    {servicesDetail.map((s, i) => (
                        <Reveal key={s.num}>
                            <article
                                data-testid={`service-detail-${i}`}
                                className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start ${
                                    i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""
                                }`}
                            >
                                <div className="lg:col-span-6">
                                    <div className="aspect-[4/5] overflow-hidden bg-charcoal-2">
                                        <img
                                            src={s.image}
                                            alt={s.title}
                                            loading="lazy"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                </div>
                                <div className="lg:col-span-6">
                                    <div className="flex items-baseline gap-4 mb-6">
                                        <span className="section-number text-3xl">{s.num}</span>
                                        <span className="overline">— {s.category}</span>
                                    </div>
                                    <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.05] text-cream mb-6">
                                        {s.title}
                                    </h2>
                                    <p className="text-[#D8D8D2] text-base md:text-lg leading-relaxed mb-10">
                                        {s.description}
                                    </p>

                                    <div className="grid grid-cols-2 gap-6 mb-10 border-t border-b border-[#262626] py-6">
                                        <div>
                                            <p className="overline mb-2">Timeline</p>
                                            <p className="font-display text-2xl text-cream">{s.timeline}</p>
                                        </div>
                                        <div>
                                            <p className="overline mb-2">Pricing</p>
                                            <p className="font-display text-2xl text-cream">{s.priceRange}</p>
                                        </div>
                                    </div>

                                    <p className="overline mb-6">— What's Included</p>
                                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                                        {s.includes.map((item) => (
                                            <li key={item} className="flex gap-3 items-start text-cream text-sm">
                                                <Check size={16} className="text-gold mt-1 shrink-0" />
                                                <span>{item}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <Link
                                        to="/contact"
                                        className="btn-primary mt-10"
                                        data-testid={`service-cta-${i}`}
                                    >
                                        Enquire {s.title} <ArrowRight size={16} />
                                    </Link>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="cta-banner border-t border-[#262626] py-24 md:py-32">
                <div className="container-pad mx-auto text-center">
                    <Reveal>
                        <h2 className="font-display text-4xl md:text-6xl text-cream max-w-3xl mx-auto leading-tight tracking-tight">
                            Where luxury meets <em className="italic text-gold">livability.</em>
                        </h2>
                        <Link to="/contact" className="btn-primary mt-10">
                            Book a Free Consultation <ArrowRight size={16} />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
